import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useApiClient } from '../useApiClient'
import { getCharts } from '../endpoints/getCharts'
import { getControls } from '../endpoints/getControls'
import { getKpisForPage } from '../endpoints/getKpisForPage'
import { fetchRatioComponentFacts } from '../endpoints/ratioComponentFacts'
import {
  walkTimeline,
  type WalkTimelineParams,
  walkAbsolute,
  type WalkAbsoluteParams,
  walkRatio,
  type WalkRatioParams,
  walkRatioAbsolute,
  type WalkRatioAbsoluteParams,
  walkWrapper,
  type WalkWrapperParams,
} from '../endpoints/walks'
import { clampFromToFloor, useModelFloorIso } from '~/state/modelFloor'

/** Reactively clamp a walk param's `from` to the model floor so the floor is part of the query
 *  identity. A window already ≥ floor is returned UNCHANGED (same reference → no extra refetch);
 *  a pre-floor window refetches once the floor loads, settling clamped (no pre-floor data). */
function useFloorClampedParams<T extends { from: string }>(params: T | null): T | null {
  const floorIso = useModelFloorIso()
  if (!params) return null
  const clampedFrom = clampFromToFloor(params.from, floorIso)
  return clampedFrom === params.from ? params : { ...params, from: clampedFrom }
}

export function usePageCharts(page: string, kpi: string | null) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['get_charts', page, kpi] as const,
    queryFn: () => getCharts(client, page, kpi!),
    enabled: kpi !== null,
  })
}

export function usePageControls(factTable: string | null) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['get_controls', factTable] as const,
    queryFn: () => getControls(client, factTable!),
    enabled: factTable !== null,
  })
}

export function useWalkTimeline(params: WalkTimelineParams | null) {
  const client = useApiClient()
  const p = useFloorClampedParams(params)
  return useQuery({
    queryKey: ['walk_timeline', p] as const,
    queryFn: () => walkTimeline(client, p!),
    enabled: p !== null,
    placeholderData: keepPreviousData,
  })
}

export function useWalkAbsolute(params: WalkAbsoluteParams | null) {
  const client = useApiClient()
  const p = useFloorClampedParams(params)
  return useQuery({
    queryKey: ['walk_absolute', p] as const,
    queryFn: () => walkAbsolute(client, p!),
    enabled: p !== null,
    placeholderData: keepPreviousData,
  })
}

export function useWalkRatio(params: WalkRatioParams | null) {
  const client = useApiClient()
  const p = useFloorClampedParams(params)
  return useQuery({
    queryKey: ['walk_ratio', p] as const,
    queryFn: () => walkRatio(client, p!),
    enabled: p !== null,
    placeholderData: keepPreviousData,
  })
}

export function useWalkRatioAbsolute(params: WalkRatioAbsoluteParams | null) {
  const client = useApiClient()
  const p = useFloorClampedParams(params)
  return useQuery({
    queryKey: ['walk_ratio_absolute', p] as const,
    queryFn: () => walkRatioAbsolute(client, p!),
    enabled: p !== null,
    placeholderData: keepPreviousData,
  })
}

export function useWalkWrapper(params: WalkWrapperParams | null) {
  const client = useApiClient()
  const p = useFloorClampedParams(params)
  return useQuery({
    queryKey: ['walk_wrapper', p] as const,
    queryFn: () => walkWrapper(client, p!),
    enabled: p !== null,
    placeholderData: keepPreviousData,
  })
}

export function useKpisForPage(page: string) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['get_kpis_for_page', page] as const,
    queryFn: () => getKpisForPage(client, page),
  })
}

/** C3b: per ratio_kpi, its num/denom component facts -- static config data (staleTime: Infinity),
 *  used by the both-sides-or-nothing campaign resolution rule. Keyed by ratio_kpi for O(1) lookup. */
export function useRatioComponentFacts() {
  const client = useApiClient()
  return useQuery({
    queryKey: ['v_ratio_component_facts'] as const,
    queryFn: async () => {
      const rows = await fetchRatioComponentFacts(client)
      return Object.fromEntries(
        rows.map((row) => [row.ratio_kpi, { numFact: row.num_fact, denomFact: row.denom_fact }]),
      ) as Record<string, { numFact: string; denomFact: string }>
    },
    staleTime: Infinity,
  })
}
